import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const cardVariants = cva('flex flex-col gap-3 rounded-xl border py-6', {
  variants: {
    variant: {
      default: 'bg-card text-card-foreground',
      // Faux "spinning gradient border": a full-bleed radial gradient in a
      // `::before` that rotates, with a white `::after` inset by the border
      // width covering the centre so only a thin animated ring shows. The
      // inner radius is the outer `rounded-xl` (12px) less that 2px inset.
      //
      // `relative` is on the variant rather than the base so the default card's
      // positioning is untouched — Alert can put it in its base because every
      // Alert has it, but plenty of Cards hold absolutely-positioned children
      // that expect to anchor further out.
      ai: `relative border-transparent bg-white/95 text-gray-900 isolate
        overflow-hidden
        before:content-[''] before:absolute before:left-1/2 before:top-1/2
        before:aspect-square before:w-[200%] before:-translate-x-1/2
        before:-translate-y-1/2 before:-z-10 before:animate-spin
        before:[animation-duration:4s]
        before:bg-[radial-gradient(circle_at_30%_30%,#6366f1_0%,#ec4899_35%,#22d3ee_65%,white_75%)]
        after:content-[''] after:absolute after:inset-[2px] after:-z-10
        after:rounded-[10px] after:bg-white/95`,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function Card({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        `@container/card-header grid auto-rows-min grid-rows-[auto_auto]
        items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto]
        [.border-b]:pb-6`,
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-foreground-dark leading-none font-semibold text-base',
        className,
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground leading-relaxed', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  )
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
}
